import { useEffect, useRef, useState } from "react";
import { MnemonicFeedbackSheet, type MnemonicFeedbackSheetHandle } from "#mnemonic-feedback-sheet";

/**
 * How long Give Feedback waits for the UI to go idle before presenting regardless. Idle time can
 * be starved while the JS thread stays busy, and the press must still get its sheet.
 */
const FEEDBACK_SHEET_IDLE_DEADLINE_MS = 250;

/**
 * Opens the mnemonic feedback sheet from a menu action. Render the returned `sheet` beside the
 * menu trigger, never inside a menu's content: that unmounts as the menu closes on the press.
 */
export function useMnemonicFeedbackSheetLauncher({
  character,
  currentMnemonic,
}: {
  character: string;
  /** The mnemonic the reader is currently looking at, sent along so feedback has its subject. */
  currentMnemonic: string;
}) {
  const feedbackSheetRef = useRef<MnemonicFeedbackSheetHandle>(null);
  // The sheet is a `BottomSheetModal`, so it needs a `BottomSheetModalProvider` above it. Mount it
  // only once feedback is actually requested: that keeps a trigger renderable on its own (and
  // costs nothing until used) instead of forcing every host of the menu to supply the provider.
  const [isFeedbackSheetMounted, setIsFeedbackSheetMounted] = useState(false);
  const shouldOpenFeedbackSheetRef = useRef(false);

  useEffect(() => {
    if (!isFeedbackSheetMounted || !shouldOpenFeedbackSheetRef.current) {
      return;
    }
    shouldOpenFeedbackSheetRef.current = false;
    feedbackSheetRef.current?.open();
  }, [isFeedbackSheetMounted]);

  const open = () => {
    // Defer presentation until the menu action has yielded to the UI, but never indefinitely.
    requestIdleCallback(
      () => {
        if (isFeedbackSheetMounted) {
          feedbackSheetRef.current?.open();
          return;
        }
        // First use: mount the sheet, then open it once its ref exists.
        shouldOpenFeedbackSheetRef.current = true;
        setIsFeedbackSheetMounted(true);
      },
      { timeout: FEEDBACK_SHEET_IDLE_DEADLINE_MS },
    );
  };

  const sheet = isFeedbackSheetMounted ? (
    <MnemonicFeedbackSheet
      currentMnemonic={currentMnemonic}
      grapheme={character}
      ref={feedbackSheetRef}
    />
  ) : null;

  return { open, sheet };
}

/** Copies the character, labelled with itself in the confirmation toast. */
export function copyMorphemeToClipboard(character: string) {
  void import("@-/dev.improve.haniverse.lib.clipboard").then(({ copyToClipboard }) =>
    copyToClipboard(character, { label: character }),
  );
}
